// React

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Box, Chip, Grid, Icon, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import { validateCUIL, validateDNI, validateEmail, validatePassword } from "utils/validations";
import ModalFinalizarProveedor from "../components/ModalFinalizarProveedor";
import { getUproColor } from "utils/colors";
// import RubrosSelector from "../ModalRubros";
import { GoogleReCaptchaProvider, withGoogleReCaptcha } from "react-google-recaptcha-v3";

function CrearCuenta({ handleCancel, googleReCaptchaProps }) {
  const { executeRecaptcha } = googleReCaptchaProps;
  const [proveedor, setProveedor] = useState({});
  const { handleSnackbar } = useContext(MessageManager);
  const [openModalFinalizar, setOpenModalFinalizar] = useState(false);

  const [addProveedorUser, { loading: loadingSave, error: errorSaveUser }] = useMutation(gql`
    mutation addUserProveedor($input: ProveedorUserInput!, $token: String!) {
      addUserProveedor(input: $input, token: $token) {
        id
      }
    }
  `);

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const { data: rubros } = useQuery(gql`
    query GET_RUBROS {
      categoriasProducto(order: { field: "nombre", order: ASC }) {
        id
        nombre
      }
    }
  `);

  const handleSave = async () => {
    if (!executeRecaptcha) {
      handleSnackbar("Error al verificar el reCAPTCHA", "error");
      return;
    }

    const token = await executeRecaptcha("register_proveedor");
    console.log("Token reCAPTCHA:", token);
    if (!token) {
      handleSnackbar("No se pudo obtener el token de reCAPTCHA", "error");
      return;
    }

    addProveedorUser({
      variables: {
        token,
        input: {
          nombre: proveedor.nombre,
          apellido: proveedor.apellido,
          username: proveedor.email,
          passwordPlain: proveedor.password,
          email: proveedor.email,
          proveedor: {
            tipoProveedor: proveedor.tipoProveedor,
            nombre: proveedor.nombre,
            apellido: proveedor.apellido,
            tipoDocumento: proveedor.tipoDocumento,
            documento: proveedor.documento,
            tipoResponsable: proveedor.tipoResponsable,
            razonSocial: proveedor.razonSocial || null,
            idProvincia: proveedor.provincia,
            idLocalidad: proveedor.localidad,
            telefono: proveedor.telefono,
            telefonoSecundario: proveedor.telefonoSecundario || null,
            referenciaContacto: proveedor.referenciaContacto || null,
            idsCategorias: proveedor.rubros,
          },
        },
      },
    })
      .then((result) => {
        handleSnackbar("Usuario creado correctamente, ya puedes iniciar sesión", "success");
        handleCancel();
      })
      .catch((error) => {
        handleSnackbar(error.message || "Error al agregar el usuario", "error");
      });
  };

  const handleChanges = (e) => {
    setProveedor({
      ...proveedor,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    if (proveedor?.provincia) {
      getLocalidades({ variables: { provincia: proveedor?.provincia } }).then((data) => {
        // Si la localidad no pertenece a la provincia seleccionada, se limpia el campo
        if (!data?.data?.localidades?.find((localidad) => localidad.id === proveedor?.localidad)) {
          setProveedor({
            ...proveedor,
            localidad: -1,
          });
        }
      });
    }
  }, [proveedor?.provincia]);

  const [openModal, setOpenModal] = useState(false);
  const [rubrosSeleccionados, setRubrosSeleccionados] = useState(proveedor.rubros || []);

  // Función para abrir el modal
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Función que actualizará los rubros seleccionados al salir del modal
  const handleRubrosChange = (rubrosSeleccionados) => {
    setRubrosSeleccionados(rubrosSeleccionados); // Actualiza el estado con los rubros seleccionados
  };
  return (
    <SoftBox p={3}>
      <SoftBox mb={2}>
        <SoftTypography variant="h6">Datos del proveedor</SoftTypography>
      </SoftBox>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="tipoProveedor">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Tipo de proveedor
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select
            name="tipoProveedor"
            value={proveedor?.tipoProveedor || -1}
            onChange={handleChanges}
          >
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            <MenuItem value="Física">Persona Física</MenuItem>
            <MenuItem value="Jurídica">Persona Jurídica</MenuItem>
          </Select>
        </Grid>
        {proveedor?.tipoProveedor === "Jurídica" && (
          <Grid item xs={12} sm={4}>
            <InputLabel htmlFor="razonSocial">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Razon Social
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="razonSocial"
              placeholder="Inserte la razón social"
              type="text"
              name="razonSocial"
              value={proveedor?.razonSocial || ""}
              onChange={handleChanges}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="nombre">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Nombre
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="nombre"
            placeholder="Inserte el nombre del proveedor"
            type="text"
            name="nombre"
            value={proveedor?.nombre || ""}
            onChange={handleChanges}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="apellido">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Apellido
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="apellido"
            placeholder="Inserte el apellido del proveedor"
            type="text"
            name="apellido"
            value={proveedor?.apellido || ""}
            onChange={handleChanges}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="tipoDocumento">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Tipo de documento
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select
            name="tipoDocumento"
            value={proveedor?.tipoDocumento || -1}
            onChange={handleChanges}
          >
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>

            <MenuItem value={96}>DNI</MenuItem>
            <MenuItem value={80}>CUIT</MenuItem>
            <MenuItem value={86}>CUIL</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="documento">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Documento
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="documento"
            placeholder="Inserte el documento del proveedor"
            type="text"
            name="documento"
            value={proveedor?.documento || ""}
            error={
              proveedor?.tipoDocumento === 96 && proveedor?.documento?.length > 0
                ? !validateDNI(proveedor?.documento)
                : proveedor?.documento?.length > 0 && !validateCUIL(proveedor?.documento)
            }
            onChange={handleChanges}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputLabel htmlFor="tipoResponsable">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Tipo de responsable
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select
            name="tipoResponsable"
            value={proveedor?.tipoResponsable || -1}
            onChange={handleChanges}
          >
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            <MenuItem value={1}>IVA Responsable Inscripto</MenuItem>
            <MenuItem value={2}>IVA Responsable no Inscripto</MenuItem>
            <MenuItem value={3}>IVA no Responsable</MenuItem>
            <MenuItem value={4}>IVA Sujeto Exento</MenuItem>
            <MenuItem value={5}>Consumidor Final</MenuItem>
            <MenuItem value={6}>Responsable Monotributo</MenuItem>
            <MenuItem value={7}>Sujeto no Categorizado</MenuItem>
            <MenuItem value={8}>Proveedor del Exterior</MenuItem>
            <MenuItem value={9}>Cliente del Exterior</MenuItem>
            <MenuItem value={10}>IVA Liberado – Ley Nº 19.640</MenuItem>
            <MenuItem value={11}>IVA Responsable Inscripto – Agente de Percepción</MenuItem>
            <MenuItem value={12}>Pequeño Contribuyente Eventual</MenuItem>
            <MenuItem value={13}>Monotributista Social</MenuItem>
            <MenuItem value={14}>Pequeño Contribuyente Eventual Social</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 6}>
          <InputLabel htmlFor="provincia">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Provincia
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="provincia" value={proveedor?.provincia || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            {provincias?.provincias?.map((provincia) => (
              <MenuItem key={provincia.id} value={provincia.id}>
                {provincia.nombre}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 6}>
          <InputLabel htmlFor="localidad">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Localidad
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="localidad" value={proveedor?.localidad || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            {localidades?.localidades?.map((localidad) => (
              <MenuItem key={localidad.id} value={localidad.id}>
                {localidad.nombre}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 4}>
          <InputLabel htmlFor="telefono">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Telefono
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="telefono"
            placeholder="Inserte un telefono de contacto"
            type="text"
            name="telefono"
            value={proveedor?.telefono}
            onChange={handleChanges}
            inputProps={{ pattern: "[0-9]{10}", title: "Debe ingresar un número de 10 dígitos" }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 4}>
          <InputLabel htmlFor="telefonoSecundario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Telefono Secundario
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="telefonoSecundario"
            placeholder="Inserte un telefono de contacto"
            type="text"
            name="telefonoSecundario"
            value={proveedor?.telefonoSecundario}
            onChange={handleChanges}
            inputProps={{ pattern: "[0-9]{10}", title: "Debe ingresar un número de 10 dígitos" }}
          />
        </Grid>
        <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 4}>
          <InputLabel htmlFor="referenciaContacto">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Referencia de contacto
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="referenciaContacto"
            placeholder="Inserte una referencia de contacto"
            type="text"
            name="referenciaContacto"
            value={proveedor?.referenciaContacto}
            onChange={handleChanges}
          />
        </Grid>

        {/* <Grid item xs={12} md={12}>
          <InputLabel htmlFor="usuario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Rubros de interés
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*&nbsp;
              </SoftTypography>
              <Tooltip
                placement="top"
                title="Los rubros de interés serán utilizados para avisar de cotizaciones abiertas que se adapten a su perfil."
              >
                <Icon color="dark">help</Icon>
              </Tooltip>
            </SoftTypography>
          </InputLabel>
          {/* Botón que abre el modal *
          <SoftButton
            variant="outlined"
            color="dark"
            onClick={handleOpenModal}
            sx={{
              width: "100%",
              color: rubrosSeleccionados.length > 0 ? "#f8f9fa" : "inherit",
              borderColor: rubrosSeleccionados.length > 0 ? "#67C1B8" : "inherit",
              padding: rubrosSeleccionados.length > 0 ? "8px 16px" : "8px",
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
              justifyContent: "flex-start",
              "@media (max-width: 600px)": {
                padding: rubrosSeleccionados.length > 0 ? "6px 12px" : "6px",
              },
              "@media (min-width: 600px) and (max-width: 960px)": {
                padding: rubrosSeleccionados.length > 0 ? "7px 14px" : "7px",
              },
            }}
          >
            {rubrosSeleccionados.length === 0 ? (
              "Seleccionar Rubros"
            ) : (
              <>
                <SoftTypography
                  variant="body2"
                  sx={{
                    fontWeight: "normal",
                    textAlign: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  Rubros seleccionados:
                </SoftTypography>

                {/* Contenedor flexible con overflow *
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    marginTop: 8,
                    overflowX: "auto", // Permitir desplazamiento horizontal si es necesario
                    paddingBottom: "8px", // Espacio en la parte inferior para scroll
                    gap: "4px", // Espaciado entre los chips
                    maxHeight: "150px", // Limitar la altura del contenedor
                  }}
                >
                  {rubrosSeleccionados.map((rubroId) => {
                    const rubro = rubros?.categoriasProducto?.find((r) => r.id === rubroId);
                    return (
                      <Chip
                        key={rubroId}
                        label={rubro?.nombre}

                        sx={{
                          fontWeight: "normal",
                          margin: "4px",
                          backgroundColor: "#67C1B8",
                          color: "#f8f9fa",
                          borderRadius: "16px",
                          fontSize: "0.75rem", // Tamaño de fuente por defecto
                          "@media (max-width: 600px)": {
                            fontSize: "0.45rem", // Reducción de tamaño para pantallas pequeñas
                          },
                          "@media (min-width: 600px) and (max-width: 960px)": {
                            fontSize: "0.70rem", // Ajustar tamaño para pantallas medianas
                          },
                        }}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </SoftButton>
          {/* Modal RubrosSelector *
          <RubrosSelector
            open={openModal}
            handleCloseCancel={handleCloseModal}
            rubros={rubros}
            handleRubrosChange={handleRubrosChange}
          />
        </Grid> */}
        <Grid item xs={12} md={4}>
          <InputLabel htmlFor="usuario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Correo electrónico
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*&nbsp;
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            type="text"
            placeholder="Correo electrónico"
            name="email"
            value={proveedor.email}
            onChange={handleChanges}
            autoComplete="new-password"
            error={proveedor.email && proveedor.email.length > 0 && !validateEmail(proveedor.email)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <InputLabel htmlFor="password">
            <Tooltip
              title={
                proveedor.confirmPassword && proveedor.password !== proveedor.confirmPassword
                  ? "Las contraseñas no coinciden"
                  : proveedor.password &&
                    proveedor.password === proveedor.confirmPassword &&
                    proveedor.password.length < 8
                  ? "La contraseña debe tener al menos 8 caracteres"
                  : proveedor.password &&
                    proveedor.password === proveedor.confirmPassword &&
                    !validatePassword(proveedor.password)
                  ? "La contraseña debe tener al menos un número"
                  : ""
              }
              placement="top"
            >
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Contraseña
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*&nbsp;
                </SoftTypography>
                {proveedor.confirmPassword && proveedor.password !== proveedor.confirmPassword ? (
                  <SoftTypography variant="caption" fontWeight="light" color="primary">
                    Las contraseñas no coinciden
                  </SoftTypography>
                ) : proveedor.password &&
                  proveedor.password === proveedor.confirmPassword &&
                  proveedor.password.length < 8 ? (
                  <SoftTypography variant="caption" fontWeight="light" color="primary">
                    La contraseña debe tener al menos 8 caracteres
                  </SoftTypography>
                ) : proveedor.password &&
                  proveedor.password === proveedor.confirmPassword &&
                  !validatePassword(proveedor.password) ? (
                  <SoftTypography variant="caption" fontWeight="light" color="primary">
                    La contraseña debe tener al menos una letra mayúscula y un número
                  </SoftTypography>
                ) : (
                  <Tooltip
                    placement="top"
                    title="La contraseña debe tener al menos 8 caracteres, al menos una letra mayúscula y un número"
                  >
                    <Icon color="dark">help</Icon>
                  </Tooltip>
                )}
              </SoftTypography>
            </Tooltip>
          </InputLabel>
          <SoftInput
            type="password"
            placeholder="Contraseña"
            name="password"
            value={proveedor.password}
            onChange={handleChanges}
            autoComplete="new-password"
            error={proveedor.confirmPassword && proveedor.password !== proveedor.confirmPassword}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <InputLabel htmlFor="confirmPassword">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Confirmar contraseña
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            type="password"
            placeholder="Contraseña"
            name="confirmPassword"
            value={proveedor.confirmPassword}
            onChange={handleChanges}
            autoComplete="new-password"
            error={proveedor.confirmPassword && proveedor.password !== proveedor.confirmPassword}
          />
        </Grid>
      </Grid>
      <SoftBox mt={4} display="flex" justifyContent="flex-end" alignItems="center">
        <SoftBox mr={2}>
          <SoftButton type="submit" color="primary" fullWidth circular onClick={handleCancel}>
            <Icon sx={{ fontWeight: "light" }}>arrow_back</Icon>
            &nbsp; Cancelar
          </SoftButton>
        </SoftBox>
        <SoftBox>
          <SoftButton
            type="submit"
            color="uproGreen"
            fullWidth
            circular
            disabled={
              loadingSave ||
              !proveedor?.password ||
              !proveedor?.confirmPassword ||
              proveedor?.password !== proveedor?.confirmPassword ||
              !validatePassword(proveedor?.password) ||
              !validateEmail(proveedor.email) ||
              !proveedor?.email ||
              !proveedor?.tipoProveedor ||
              !proveedor?.nombre ||
              !proveedor?.apellido ||
              !proveedor?.tipoDocumento ||
              !proveedor?.documento ||
              !proveedor?.tipoResponsable ||
              !proveedor?.provincia ||
              !proveedor?.localidad ||
              proveedor?.localidad === -1 ||
              (proveedor?.tipoDocumento === 96 && proveedor?.documento?.length > 0
                ? !validateDNI(proveedor?.documento)
                : proveedor?.documento?.length > 0 && !validateCUIL(proveedor?.documento)) ||
              (proveedor?.tipoProveedor === "Jurídica" && !proveedor?.razonSocial) ||
              !proveedor?.telefono
              //proveedor?.rubros?.length < 1
            }
            onClick={() => {
              setOpenModalFinalizar(true);
            }}
          >
            Crear cuenta &nbsp;
            <Icon sx={{ fontWeight: "light" }}>arrow_forward</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
      <ModalFinalizarProveedor
        open={openModalFinalizar}
        handleClose={() => setOpenModalFinalizar(false)}
        handleSave={handleSave}
      />
    </SoftBox>
  );
}

CrearCuenta.defaultProps = {
  handleCancel: () => {},
};

CrearCuenta.propTypes = {
  handleCancel: PropTypes.func,
  googleReCaptchaProps: PropTypes.object.isRequired,
};

const WrappedCrearCuentaProveedor = withGoogleReCaptcha(CrearCuenta);

function Capchat({ handleCancel }) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
      <WrappedCrearCuentaProveedor handleCancel={handleCancel} />
    </GoogleReCaptchaProvider>
  );
}

Capchat.propTypes = {
  handleCancel: PropTypes.func.isRequired,
};

export default Capchat;
